import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { yearlyPriceOnMonthlyTariffButtonTouch } from 'reactApp/appHelpers/featuresHelpers';
import { enabledFamilyTariff } from 'reactApp/appHelpers/featuresHelpers/features/familyTariff';
import { yearPriceInMonthEnabled } from 'reactApp/appHelpers/featuresHelpers/features/yearPriceInMonth';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { getMultiTariffMobileActiveTab } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { normalizeMobileTariffList } from 'reactApp/sections/MobileSubscriptionsPage/MobileSubscriptionsPage.helpers';
import { TariffCard } from 'reactApp/sections/MobileSubscriptionsPage/TariffCard/TariffCard';
import { type MobileBuyClickType, MobileTariffCardTheme } from 'reactApp/sections/MobileSubscriptionsPage/TariffCard/TariffCard.types';
import { useGetProductsSpaces } from 'reactApp/sections/QuotaLanding/MultiTariffsListMobile/hooks/useGetProductsSpaces';
import { MobileTariffCardLoader } from 'reactApp/sections/QuotaLanding/TariffsList/MobileTariffCardLoader/MobileTariffCardLoader';
import type { Product } from 'reactApp/types/Billing';
import { ETabsName } from 'reactApp/ui/TariffSectionNew/TariffSection.constants';

import styles from './VerticalTariffCardList.css';

interface Props {
    onBuy: MobileBuyClickType;
}

export const VerticalTariffCardList = memo(({ onBuy }: Props) => {
    const activeTariffTab = useSelector(getMultiTariffMobileActiveTab);
    const { spaces, products, selectorDataLoaded = false } = useGetProductsSpaces();
    const { isLoaded } = useSelector(ProductsSelectors.getLifeCycleState);

    const tariffList = useMemo(() => {
        const mobileTariffList = spaces.reduce((acc, space) => {
            const { yearProduct, monthProduct } = products?.[space] || {};
            if (yearProduct) {
                acc[yearProduct.id] = [yearProduct, monthProduct];
            }
            return acc;
        }, {}) as Record<string, [Product, Product | undefined, Product | undefined]>;

        return normalizeMobileTariffList(mobileTariffList, true);
    }, [spaces, products]);

    return (
        <>
            <div className={styles.mobileList}>
                {tariffList.map((tariff, index) => (
                    <div className={styles.mobileTariff} key={tariff.products?.[0].id}>
                        <TariffCard
                            {...(tariff as any)}
                            onClick={onBuy}
                            isTopCard={index === 0}
                            theme={MobileTariffCardTheme.grey}
                            isSingleQuota
                            showPriceByMonth={yearlyPriceOnMonthlyTariffButtonTouch === 'c' || yearPriceInMonthEnabled === 'b'}
                            skipBenefitsFeature
                            isRebrandingQuotaLanding
                            isFamily={Boolean(enabledFamilyTariff && tariff?.isFamily)}
                            withoutFeatures
                            isMultiTariff
                            isCardActive={index === 0 && activeTariffTab !== ETabsName.basic}
                            onlySpace={activeTariffTab === ETabsName.basic}
                        />
                    </div>
                ))}
                {(!selectorDataLoaded || !isLoaded) &&
                    Array.from(new Array(6)).map((_, idx) => (
                        <div className={styles.mobileTariff} key={`mobileCard_${idx}`}>
                            <MobileTariffCardLoader isExpand={false} isDark isTariffsPage headerHeight={200} />
                        </div>
                    ))}
            </div>
        </>
    );
});

VerticalTariffCardList.displayName = 'VerticalTariffCardList';
